import { debounce } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { RicosContext } from 'ricos-context';
import type { Helpers, LinkPreviewData } from 'ricos-types';
import { Node_Type } from 'ricos-types';
import type { ComponentData } from 'wix-rich-content-common';
import { getHost } from 'wix-rich-content-common';
import HtmlComponent from 'wix-rich-content-plugin-html/libs/HtmlComponent';
import { Image } from 'wix-rich-content-ui-components';

import styles from '../../statics/styles/link-preview.scss';
import type { LinkPreviewPluginEditorConfig } from '../types';
import { LINK_PREVIEW_TYPE } from '../types';

export const LinkPreviewViewer: React.FC<{
  componentData: ComponentData;
  settings: LinkPreviewPluginEditorConfig;
  iframeSandboxDomain: string;
  nodeId: string;
  helpers?: Helpers;
}> = ({ componentData, settings, iframeSandboxDomain, helpers, nodeId }) => {
  const { enableEmbed = true } = settings;

  return enableEmbed && componentData.oembed?.html ? (
    <SocialEmbedViewer
      componentData={componentData}
      settings={settings}
      iframeSandboxDomain={iframeSandboxDomain}
    />
  ) : (
    <LinkPreviewCard nodeId={nodeId} componentData={componentData} helpers={helpers} />
  );
};

export const SocialEmbedViewer: React.FC<{
  componentData: ComponentData;
  settings: LinkPreviewPluginEditorConfig;
  iframeSandboxDomain: string;
}> = ({ componentData, settings, iframeSandboxDomain }) => {
  const { theme, isMobile } = useContext(RicosContext);

  const htmlCompProps = {
    componentData: {
      ...componentData,
      html: unescape(componentData.oembed.html),
    },
    settings,
    theme,
    isMobile,
    iframeSandboxDomain,
  };
  return <HtmlComponent {...htmlCompProps} />;
};

export const LinkPreviewCard: React.FC<{
  componentData: ComponentData;
  helpers?: Helpers;
  nodeId: string;
}> = ({ componentData, helpers, nodeId }) => {
  const [imageHeight, setImageHeight] = useState<number>(0);
  const [elementRef, setElementRef] = useState<HTMLElement | null>(null);
  const { isMobile } = useContext(RicosContext);
  const linkPreviewStyles = styles;

  useEffect(() => {
    if (!elementRef || !window?.ResizeObserver) {
      return;
    }

    const resizer = new ResizeObserver(
      debounce(entries => {
        setImageHeight(Math.round(entries[0].contentRect.height));
      }, 60)
    );
    resizer.observe(elementRef);

    return () => {
      resizer.unobserve(elementRef);
    };
  }, [elementRef]);

  const getUrlForDisplay = (url: string) => url.replace(/^https?:\/\//, '');

  const onLinkPreviewClick = () =>
    helpers?.onViewerAction?.(LINK_PREVIEW_TYPE, 'Click', componentData.link.url || '', nodeId, {
      type: Node_Type.LINK_PREVIEW,
      linkPreviewData: componentData as LinkPreviewData,
    });

  const { title, description, thumbnailUrl } = componentData;
  const {
    linkPreview,
    linkPreviewUrl,
    linkPreviewInfo,
    linkPreviewTitle,
    linkPreviewImage,
    linkPreviewDescription,
  } = linkPreviewStyles;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <figure
      className={linkPreview}
      data-hook="linkPreviewViewer"
      onClick={onLinkPreviewClick}
      ref={setElementRef}
    >
      {thumbnailUrl && (
        <Image
          uri={thumbnailUrl}
          containerProps={{
            style: {
              height: imageHeight,
              width: isMobile ? 110 : imageHeight,
            },
            className: linkPreviewImage,
          }}
          height={imageHeight}
          width={isMobile ? 110 : imageHeight}
          displayMode="fill"
          alt={title}
        />
      )}
      <section className={linkPreviewInfo}>
        <div className={linkPreviewUrl}>
          {getUrlForDisplay(getHost(componentData.link.url) || componentData.link?.url)}
        </div>
        <figcaption className={linkPreviewTitle}>{title}</figcaption>
        {description && <div className={linkPreviewDescription}>{description}</div>}
      </section>
    </figure>
  );
};
